import { effect, signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { catchError, combineLatest, EMPTY, filter, Subject, Subscription, take, takeUntil, tap, timeout } from 'rxjs';
import { DataType } from '../../../dataModel/DataType';
import { DataQueryExpr } from '../../../dataQuery';
import { SyntaxSpan } from '../../../dataQuery/expr';
import { Predicate, PredicateType } from '../../../utils/Predicate';
import { RowsResult, VisualDataResource } from '../../data';
import { VisualDataAdapter } from '../../data/VisualDataAdapter';
import { AgGridPivotTableVisualComponent } from '../pivotTable/PivotTableVisual.component';
import { Pivot, PivotGroup, PivotValue, PivotValueGroup, ProcessNewRowsGroup, ProcessNewRowsGroups } from './pivot';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { dataComparer } from './dataComparer';
import { SortDirection } from 'src/app/services/api.services';

export class DataSource {
  readonly data = this.component.data;
  readonly injector = this.component.injector;

  destroyed = false;
  destroyed$ = new Subject<void>();

  get pivot() {
    return this.component.pivot();
  };

  private order() {
    return this.pivot.valueGroups
      .map(x => x.orderBy())
      .filter(x => !!x);
  }

  constructor(readonly component: AgGridPivotTableVisualComponent) {
    effect(() => {
      if (this.destroyed)
        return;

      let [table, order] = [this.data.table(), this.order()];
      if (table) {
        untracked(() => this.updateServerSideDatasourceAdapter(true));
      }
    }, { injector: this.injector });
  }

  destroy() {
    if (this.destroyed)
      return;

    this.destroyed = true;
    this.destroyed$.next();
  }

  readonly serverSideDatasourceAdapter = signal<ServerSideDatasourceAdapter>(null);

  private updateServerSideDatasourceAdapter(force?: boolean) {
    let adapter = this.serverSideDatasourceAdapter();

    if (!force) {
      if (adapter && !adapter.destroyed)
        return;
    }

    try {
      let adapter = new ServerSideDatasourceAdapter(this, this.order());
      this.serverSideDatasourceAdapter.set(adapter);
    }
    finally {
      adapter?.destroy();
    }
  }
}

class ServerSideDatasourceAdapter implements IServerSideDatasource {
  destroyed = false;

  private subscriptions = new Subscription();
  private pending = new Set<IServerSideGetRowsParams>();

  constructor(
    readonly dataSource: DataSource,
    readonly order: VisualDataAdapter.OrderByList) { }

  getRows(params: IServerSideGetRowsParams<any, any>) {
    if (this.destroyed)
      return;

    untracked(() => {
      this.pending.add(params);

      try {
        let { resource, pivotGroups } = this.getRequest(params);

        this.subscriptions ??= new Subscription();
        this.subscriptions.add(this.processRequest(resource, pivotGroups, params).subscribe());
      }
      catch (err) {
        console.error(err);

        if (!this.pending.has(params))
          return;

        this.pending.delete(params);

        try {
          params.fail();
        }
        catch (err) {
          console.error(err);
        }
      }
    });
  }

  private getRequest(params: IServerSideGetRowsParams<any, any>) {
    let { 
      rowGroupCols, groupKeys, 
      pivotCols, valueCols,
    } = params.request;

    let table = this.dataSource.data.table();

    let pivot   = this.dataSource.pivot;
    let pivoted = pivot.columnGroups.length > 0;

    let rowPivotGroups = rowGroupCols.map(getPivotGroup).slice(0, groupKeys.length + 1);

    let [columnPivotGroups, valuePivotGroups] =
      pivoted ? [
        pivotCols.map(getPivotGroup),
        valueCols.map(getPivotGroup)
      ] : [
        [],
        pivot.valueGroups.map(getPivotGroup)
      ];

    let pivotGroups: ProcessNewRowsGroups = {
      rows:    rowPivotGroups,
      columns: columnPivotGroups,
      values:  valuePivotGroups,
    };

    let requestFields = {
      rows:     pivotGroups.rows   .filter(x => !!x.group.vf).map(createRequestField),
      columns:  pivotGroups.columns.filter(x => !!x.group.vf).map(createRequestField),
      measures: pivotGroups.values .filter(x => !!x.group.vf).map(createRequestField),
    };

    let groupings = requestFields
      .rows
      .map(createGrouping);

    let columns = requestFields
      .columns
      .map(createGrouping);

    let measures = requestFields
      .measures
      .map(createSumMeasure);

    let filters = groupKeys
      .map((x, i) => createIsFilter(requestFields.rows[i], groupKeys[i]));

    let resource = table.calculate({
      abortSignal: this.dataSource.destroyed$,
      debugName: `getRequest`,

      fields: [
        ...groupings,
        ...columns
      ],

      measures,
      filters,
    });

    return {
      resource,
      pivotGroups
    };
  

    function getPivotGroup(f: { id: any; } | PivotGroup): ProcessNewRowsGroup<any> {
      let group: PivotGroup;

      if (f instanceof PivotGroup) {
        group = f;
      }
      else {
        group = <PivotGroup>params.api.getColumnDef(f.id).context;
        if (!(group instanceof PivotGroup))
          return null;
      }

      return {
        group:     params.api.getColumnDef(f.id).context,
        dataName:  group.dataField,
        dataIndex: -1,
      };
    }

    function createRequestField(f: ProcessNewRowsGroup): RequestField {
      return {
        field:    f,
        expr:     table.columns.get(f.group.vf).identifier,
        name:     f.dataName,
        dataType: f.group.vf.dataType,
      };
    }

    function createGrouping(f: RequestField): VisualDataAdapter.FieldCalculation {
      return {
        name:     f.name,
        expr:     f.expr,
        dataType: f.dataType,
        return:   true,
        grouping: true,
      };
    }
    
    function createSumMeasure(f: RequestField): VisualDataAdapter.Calculation {
      return {
        name:     f.name,
        expr:     `AGGR_SUM(${f.expr})`,
        dataType: f.dataType,
        return:   true,
      };
    }

    function createIsFilter(f: RequestField, value: any): VisualDataAdapter.FieldFilter {
      let fieldExpr = DataQueryExpr.parse(f.expr);

      let filterValue = 
        value          instanceof PivotValue ? value.raw :
        value?.context instanceof PivotValue ? value.context.raw : value;

      let filterValueSyntax = DataQueryExpr.createLiteralSyntax(f.dataType.literalType, filterValue);

      let filterSyntax;

      if (isNullOrUndefined(filterValue)) {
        filterSyntax = new DataQueryExpr.FunctionCallSyntax(
          SyntaxSpan.empty,
          'IS_NULL',
          [filterValueSyntax]);
      }
      else {
        filterSyntax = new DataQueryExpr.BinarySyntax(
          SyntaxSpan.empty,
          DataQueryExpr.BinaryOperator.Equal,
          fieldExpr,
          filterValueSyntax);
      }

      return <Predicate<any>>{
        type: PredicateType.Expr,
        expr: filterSyntax.toString()
      };
    }
  }


  private processRequest(
    request: VisualDataResource<RowsResult>,
    groups: ProcessNewRowsGroups,
    params: IServerSideGetRowsParams<any, any>) {
    let paramsCompleted = false;

    return combineLatest({
      completed: toObservable(request.completed, { injector: this.dataSource.injector })
    }).pipe(
      takeUntil(this.dataSource.destroyed$),
      catchError(err => {
        console.error(err);

        if (this.destroyed)
          return;

        if (!paramsCompleted) {
          params.fail();
        }

        return EMPTY;
      }),

      filter(({ completed }) => completed),
      take(1),
      tap(({ completed }) => {
        if (this.destroyed)
          return;

        if (!this.pending.has(params)) {
          paramsCompleted = true;
          return;
        }

        this.pending.delete(params);

        if (request.success()) {
          let result = request.result();

          [groups.rows, groups.columns, groups.values].forEach(x => {
            x.forEach((g: ProcessNewRowsGroup<PivotGroup>) => {
              if (!g.group.vf)
                return;

              g.dataIndex = result.columns.find(c => c.name.toLowerCase() == g.dataName)?.dataIndex;
              if (g.dataIndex === undefined)
                throw new Error(`Missing column in result '${g.dataName}'`);
            });
          });

          let pivot = this.dataSource.pivot;
          let { rowData, rowCount } = pivot.processNewRows(groups, <any>result.rows);

          let vf = rowData.find((r) => !!r.rowGroup?.vf)?.rowGroup?.vf;
          if(vf)
            rowData.sort((a, b) => dataComparer.getComparerForField(vf)(a.ownValue?.raw, b.ownValue?.raw));

          this.dataSource.component.ag.pendingSortUpdate = true;

          params.success({
            rowData,
            rowCount,
          });
        }
        else {
          params.fail();
        }

        paramsCompleted = true;
      })
    );
  }


  destroy() {
    if (this.destroyed)
      return;

    this.destroyed = true;

    try {
      this.subscriptions?.unsubscribe();
      this.subscriptions = null;
    }
    catch (err) {
      console.error(err);
    }
    finally {
      this.pending.forEach(x => {
        try {
          x.fail();
        }
        catch (err) {
          console.error(err);
        }
      });

      this.pending.clear();
    }
  }
}

interface RequestField {
  readonly field: any;
  readonly name: string;
  readonly expr: string;
  readonly dataType: DataType;
}
